import { useEffect, useState } from 'react';
import { Eye, Plus, X } from 'lucide-react';
import type { GuestDto, ProjectSnapshotDto, TaskDto, TaskTablePageDto } from '@werft/contracts';
import { t } from '../../i18n/index.js';
import { api, newOpId } from '../../platform/api.js';
import { keys, queryClient, useGuests, useProjectSnapshot } from '../../platform/queries.js';
import { endPreview, isPlanned, preview } from '../../platform/preview.js';
import { toast } from '../../platform/toast.js';
import { Button, Input } from '../../ui/ui.js';

/**
 * Gäste feiner (P21, ADR 0024): einzelne Aufgaben freigeben, „nur zugewiesene“, Vorschau „Als Gast ansehen“.
 */

/** Banner im Vorschau-Tab: wessen Sicht, nur lesen, beenden */
export function PreviewBanner() {
  const p = preview();
  const snap = useProjectSnapshot(p?.projectId ?? '', !!p);
  if (!p) return null;
  const name = snap.data?.members.find((m) => m.userId === p.userId)?.name ?? t('guests.guest');
  const planned = isPlanned(p.userId);
  return (
    <div role="status" className="flex flex-wrap items-center gap-3 bg-amber-100 px-4 py-2 text-sm text-amber-950 dark:bg-amber-950 dark:text-amber-100">
      <Eye className="size-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1" data-preview-banner={planned ? 'planned' : 'guest'}>
        {planned ? t(`preview.planned.${p.userId.split(':')[1] as 'view' | 'comment' | 'edit'}`) : t('preview.banner', { name })}
      </span>
      <Button variant="secondary" onClick={() => endPreview(p.projectId)}>
        {t('preview.end')}
      </Button>
    </div>
  );
}

/** „Als Gast ansehen“: eigener Tab mit der Sicht des Gastes */
export function openPreview(projectId: string, userId: string) {
  window.open(`/p/${projectId}?als=${encodeURIComponent(userId)}`, '_blank', 'noopener');
}

/** Aufgaben suchen und auswählen (für die Gast-Freigabe) */
export function TaskPicker({
  projectId,
  selected,
  onChange,
  exclude = [],
}: {
  projectId: string;
  selected: { id: string; title: string }[];
  onChange: (tasks: { id: string; title: string }[]) => void;
  /** P26: nicht anbieten (z. B. die Aufgabe selbst) */
  exclude?: string[];
}) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<TaskDto[]>([]);
  useEffect(() => {
    if (q.trim().length < 2) return setHits([]);
    const id = setTimeout(() => {
      void api
        .get<TaskTablePageDto>(`/api/projects/${projectId}/table?state=all&limit=8&q=${encodeURIComponent(q.trim())}`)
        .then((r) => setHits(r.items))
        .catch(() => setHits([]));
    }, 200);
    return () => clearTimeout(id);
  }, [q, projectId]);
  const chosen = new Set(selected.map((s) => s.id));
  const autoFocus = exclude.length > 0;
  return (
    <div className="space-y-2">
      {selected.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label={t('guests.chosenTasks')}>
          {selected.map((s) => (
            <li key={s.id} className="flex items-center gap-1 rounded-full bg-zinc-100 py-0.5 pr-1 pl-3 text-sm dark:bg-zinc-800">
              <span className="max-w-48 truncate">{s.title}</span>
              <button type="button" aria-label={t('guests.removeTask', { title: s.title })} onClick={() => onChange(selected.filter((x) => x.id !== s.id))} className="grid size-7 place-items-center rounded-full hover:bg-zinc-200 dark:hover:bg-zinc-700">
                <X className="size-3.5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
      <Input autoFocus={autoFocus} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('guests.searchTask')} aria-label={t('guests.searchTask')} />
      {hits.length > 0 && (
        <ul className="space-y-0.5">
          {hits
            .filter((h) => !chosen.has(h.id) && !exclude.includes(h.id))
            .map((h) => (
              <li key={h.id}>
                <button type="button" onClick={() => (onChange([...selected, { id: h.id, title: h.title }]), setQ(''))} className="flex min-h-10 w-full items-center gap-2 rounded-lg px-2 text-left text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800">
                  <Plus className="size-4 shrink-0" aria-hidden />
                  <span className="truncate">{h.title}</span>
                </button>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}

/** Aufgabendetails (Projektleitung): diese Aufgabe einzelnen Gästen freigeben */
export function TaskGuests({ task, snapshot }: { task: TaskDto; snapshot: ProjectSnapshotDto }) {
  const hasGuests = snapshot.members.some((m) => m.role === 'guest');
  const guests = useGuests(snapshot.project.id, hasGuests && snapshot.project.role === 'lead');
  const list = guests.data?.guests ?? [];
  // sofort zeigen, was gewählt wurde; der Server bestätigt (oder setzt zurück)
  const [local, setLocal] = useState<Record<string, boolean>>({});
  if (!hasGuests || snapshot.project.role !== 'lead' || !list.length) return null;
  async function toggle(g: GuestDto, on: boolean) {
    setLocal((x) => ({ ...x, [g.userId]: on }));
    try {
      await api.put(`/api/projects/${snapshot.project.id}/guests/${g.userId}/tasks/${task.id}`, { opId: newOpId(), on });
    } catch (e) {
      toast((e as Error).message, { kind: 'error' });
    }
    await queryClient.invalidateQueries({ queryKey: keys.project(snapshot.project.id) });
    setLocal((x) => {
      const { [g.userId]: _done, ...rest } = x;
      return rest;
    });
  }
  return (
    <fieldset className="space-y-1">
      <legend className="text-sm font-medium">{t('guests.forTask')}</legend>
      {list.map((g) => {
        const granted = local[g.userId] ?? g.tasks.some((x) => x.id === task.id);
        const viaScope = !granted && g.wholeProject && !g.onlyAssigned;
        return (
          <label key={g.userId} className="flex min-h-10 items-center gap-3 text-sm">
            <input type="checkbox" checked={granted || viaScope} disabled={viaScope} onChange={(e) => void toggle(g, e.target.checked)} className="size-4 accent-[var(--color-accent)]" />
            <span className="min-w-0 flex-1 truncate">{g.name}</span>
            {viaScope && <span className="text-xs text-zinc-500">{t('guests.viaProject')}</span>}
          </label>
        );
      })}
    </fieldset>
  );
}
